import { useState, useEffect, useRef } from "react";
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  Checkbox,
  Select,
  Divider,
  Box,
  Paper,
  Badge,
  TextInput,
  ScrollArea,
  Alert,
} from "@mantine/core";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { useForm } from "@mantine/form";
import { DateInput } from "@mantine/dates";
import {
  IconInbox,
  IconCheck,
  IconFlask,
  IconCube,
  IconScan,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import {
  SendOut,
  SendoutRequestType,
  sendOutsService,
} from "../../services/sendOuts.service";
import DocumentUpload from "../accessioning/DocumentUpload";
import type { UploadedFile } from "../../types";
import { useLogReceipt } from "../../hooks/useSendOuts";
import { useUsersAdmin } from "../../hooks/admin/useUsers";
import { useAuthStore } from "../../store";
import {
  required,
  scrollToFirstError,
  showValidationNotification,
} from "../../utils/formValidation";

interface LogReceiptModalProps {
  opened: boolean;
  onClose: () => void;
  sendOut: SendOut | null;
  onSuccess?: () => void;
}

interface ReceiptFormValues {
  slidesReturned: string[];
  blocksReturned: string[];
  itemConditions: Record<string, string>;
  receivedById: string;
  receivedDate: Date | null;
  qaReviewerId: string;
  externalDiagnosis: string;
  externalReferenceNumber: string;
  externalInterpretation: string;
  notes: string;
  stainResult: string;
  stainResultText: string;
}

const CONDITION_OPTIONS = [
  { value: "good", label: "Good" },
  { value: "damaged", label: "Damaged" },
];

function getConditionBadgeColor(condition?: string): string {
  if (condition === "damaged") return "red";
  return "green";
}

function getReceiptRequestTypeLabel(requestType?: SendoutRequestType): string {
  if (requestType === SendoutRequestType.MATERIAL_RETURN)
    return "Material Return";
  if (requestType === SendoutRequestType.EXTERNAL_INSTITUTION)
    return "External Request";
  return "External Opinion";
}

export default function LogReceiptModal({
  opened,
  onClose,
  sendOut,
  onSuccess,
}: LogReceiptModalProps) {
  const { profile } = useAuthStore();
  const { data: users = [] } = useUsersAdmin();
  const logReceiptMutation = useLogReceipt();
  const [view, setView] = useState<"form" | "confirm">("form");
  const [pendingValues, setPendingValues] = useState<ReceiptFormValues | null>(
    null
  );
  const [reportFiles, setReportFiles] = useState<UploadedFile[]>([]);
  const [scanInput, setScanInput] = useState("");
  const isUpdatingNotesEditorRef = useRef(false);

  const notesEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: "Any additional notes about the receipt...",
      }),
      Underline,
    ],
    content: "",
  });

  const diagnosisEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        code: false,
        blockquote: false,
        horizontalRule: false,
      }),
      Placeholder.configure({
        placeholder: "Summary of the external pathologist's diagnosis...",
      }),
      Underline,
    ],
    content: "",
  });

  const isExternalInstitution =
    sendOut?.requestType === SendoutRequestType.EXTERNAL_INSTITUTION ||
    sendOut?.requestType === SendoutRequestType.MATERIAL_RETURN;
  const isExternalStain = isExternalInstitution;

  const form = useForm<ReceiptFormValues>({
    initialValues: {
      slidesReturned: [],
      blocksReturned: [],
      itemConditions: {},
      receivedById: "",
      receivedDate: new Date(),
      qaReviewerId: "",
      externalDiagnosis: "",
      externalReferenceNumber: "",
      externalInterpretation: "",
      notes: "",
      stainResult: "",
      stainResultText: "",
    },
    validate: {
      receivedById: required("Received by is required"),
      receivedDate: (value) => (value ? null : "Received date is required"),
    },
  });
  const setReceiptFormValues = form.setValues;
  const resetReceiptForm = form.reset;

  useEffect(() => {
    if (opened && sendOut) {
      resetReceiptForm();
      setView("form");
      setPendingValues(null);
      setReportFiles([]);
      if (notesEditor) {
        isUpdatingNotesEditorRef.current = true;
        notesEditor.commands.setContent("");
        isUpdatingNotesEditorRef.current = false;
      }
      if (diagnosisEditor) {
        diagnosisEditor.commands.setContent("");
      }
      // Pre-check all items (previously returned ones will be disabled)
      const allIds = [
        ...(sendOut.slidesSent || []),
        ...(sendOut.blocksSent || []),
      ];
      const defaultConditions: Record<string, string> = {};
      allIds.forEach((id) => {
        defaultConditions[id] = "good";
      });
      setReceiptFormValues({
        slidesReturned: sendOut.slidesReturned || [],
        blocksReturned: sendOut.blocksReturned || [],
        itemConditions: defaultConditions,
        qaReviewerId: sendOut.qaReviewer?.id || "",
        receivedById: profile?.id || "",
      });
    }
  }, [
    diagnosisEditor,
    notesEditor,
    opened,
    profile?.id,
    resetReceiptForm,
    sendOut,
    setReceiptFormValues,
  ]);

  if (!sendOut) return null;

  const slidesSent = sendOut.slidesSent || [];
  const blocksSent = sendOut.blocksSent || [];
  const previouslyReturnedSlides = new Set(sendOut.slidesReturned || []);
  const previouslyReturnedBlocks = new Set(sendOut.blocksReturned || []);
  const totalSent = slidesSent.length + blocksSent.length;
  const totalReturned =
    form.values.slidesReturned.length + form.values.blocksReturned.length;

  const uploadedFileKey = reportFiles.length > 0 ? reportFiles[0].s3Key : null;
  const uploadedFileName =
    reportFiles.length > 0
      ? reportFiles[0].file?.name || reportFiles[0].fileName || null
      : null;

  // Build barcode-to-id maps from resolved materials
  // Barcode formats:
  //   Block: {caseNumber};{specimenLetter};{blockNumber} e.g., "G26-00900;A;1"
  //   Slide: {caseNumber};{specimenLetter};{blockNumber};{slideNumber} e.g., "G26-00900;A;1;1"
  const barcodeToSlideId = new Map<string, string>();
  const barcodeToBlockId = new Map<string, string>();
  const resolvedMaterials = (sendOut as any)?.resolvedMaterials || [];
  const caseNumber = sendOut.case?.caseNumber || "";

  resolvedMaterials.forEach((item: any) => {
    const specimenLetter = item.specimen.number; // e.g., "A"
    item.blocks.forEach((block: any) => {
      // Build block barcode: {caseNumber};{specimenLetter};{blockNumber}
      if (caseNumber) {
        const blockBarcode = `${caseNumber};${specimenLetter};${block.number}`;
        barcodeToBlockId.set(blockBarcode.toLowerCase(), block.id);
      }

      block.slides.forEach((slide: any) => {
        // Use stored barcode if available
        if (slide.barcode) {
          barcodeToSlideId.set(slide.barcode.toLowerCase(), slide.id);
        }
        // Also build slide barcode from case/specimen/block/slide numbers
        if (caseNumber) {
          const slideBarcode = `${caseNumber};${specimenLetter};${block.number};${slide.number}`;
          barcodeToSlideId.set(slideBarcode.toLowerCase(), slide.id);
        }
      });
    });
  });

  // Helper: Get all slide IDs for a block that are in slidesSent
  const getBlockSlideIds = (block: any): string[] => {
    return block.slides
      .map((s: any) => s.id)
      .filter((id: string) => slidesSent.includes(id));
  };

  // Handle block checkbox change - selects/deselects all slides in the block
  const handleBlockChange = (block: any, checked: boolean) => {
    const blockSlideIds = getBlockSlideIds(block);

    if (checked) {
      // Add block and all its slides
      const newBlocks = Array.from(
        new Set([...form.values.blocksReturned, block.id])
      );
      const newSlides = Array.from(
        new Set([
          ...form.values.slidesReturned,
          ...blockSlideIds.filter((id) => !previouslyReturnedSlides.has(id)),
        ])
      );
      const newConditions = { ...form.values.itemConditions };
      if (!newConditions[block.id]) newConditions[block.id] = "good";
      blockSlideIds.forEach((id) => {
        if (!newConditions[id]) newConditions[id] = "good";
      });
      form.setValues({
        ...form.values,
        blocksReturned: newBlocks,
        slidesReturned: newSlides,
        itemConditions: newConditions,
      });
    } else {
      // Remove block and all its slides
      form.setValues({
        ...form.values,
        blocksReturned: form.values.blocksReturned.filter(
          (id) => id !== block.id
        ),
        slidesReturned: form.values.slidesReturned.filter(
          (id) => !blockSlideIds.includes(id)
        ),
      });
    }
  };

  const handleBarcodeScan = () => {
    const barcode = scanInput.trim();
    if (!barcode) return;

    const normalizedBarcode = barcode.toLowerCase();

    // First, check if it's a slide barcode
    const slideId = barcodeToSlideId.get(normalizedBarcode);
    if (slideId) {
      if (previouslyReturnedSlides.has(slideId)) {
        notifications.show({
          title: "Already Returned",
          message: "This slide was already returned in a previous receipt",
          color: "yellow",
        });
        setScanInput("");
        return;
      }

      if (form.values.slidesReturned.includes(slideId)) {
        notifications.show({
          title: "Already Checked",
          message: "This slide is already marked as returned",
          color: "yellow",
        });
        setScanInput("");
        return;
      }

      // Mark slide as returned
      form.setFieldValue("slidesReturned", [
        ...form.values.slidesReturned,
        slideId,
      ]);
      // Set default condition
      form.setFieldValue("itemConditions", {
        ...form.values.itemConditions,
        [slideId]: "good",
      });

      notifications.show({
        title: "Slide Scanned",
        message: "Slide marked as returned",
        color: "green",
      });
      setScanInput("");
      return;
    }

    // Check if it's a block barcode
    const blockId = barcodeToBlockId.get(normalizedBarcode);
    if (blockId) {
      if (previouslyReturnedBlocks.has(blockId)) {
        notifications.show({
          title: "Already Returned",
          message: "This block was already returned in a previous receipt",
          color: "yellow",
        });
        setScanInput("");
        return;
      }

      // Find the block object from resolvedMaterials
      const blockObj = resolvedMaterials
        .flatMap((item: any) => item.blocks)
        .find((b: any) => b.id === blockId);

      if (blockObj) {
        // Use handleBlockChange to select block and all its slides
        handleBlockChange(blockObj, true);

        notifications.show({
          title: "Block Scanned",
          message: "Block and all its slides marked as returned",
          color: "green",
        });
      } else {
        // Fallback: just mark block as returned (no slides info available)
        form.setFieldValue("blocksReturned", [
          ...form.values.blocksReturned,
          blockId,
        ]);
        form.setFieldValue("itemConditions", {
          ...form.values.itemConditions,
          [blockId]: "good",
        });

        notifications.show({
          title: "Block Scanned",
          message: "Block marked as returned",
          color: "green",
        });
      }
      setScanInput("");
      return;
    }

    // No match found
    notifications.show({
      title: "Not Found",
      message: `No matching slide or block found for barcode "${barcode}"`,
      color: "red",
    });
    setScanInput("");
  };

  // Helper: Get all block IDs for a specimen that are in blocksSent
  const getSpecimenBlockIds = (item: any): string[] => {
    return item.blocks
      .map((b: any) => b.id)
      .filter((id: string) => blocksSent.includes(id));
  };

  // Helper: Get all slide IDs for a specimen (from all its blocks) that are in slidesSent
  const getSpecimenSlideIds = (item: any): string[] => {
    return item.blocks.flatMap((block: any) => getBlockSlideIds(block));
  };

  // Handle specimen checkbox change - selects/deselects all blocks and slides
  const handleSpecimenChange = (item: any, checked: boolean) => {
    const specimenBlockIds = getSpecimenBlockIds(item);
    const specimenSlideIds = getSpecimenSlideIds(item);

    if (checked) {
      // Add all blocks and slides (that aren't previously returned)
      const newBlocks = Array.from(
        new Set([
          ...form.values.blocksReturned,
          ...specimenBlockIds.filter((id) => !previouslyReturnedBlocks.has(id)),
        ])
      );
      const newSlides = Array.from(
        new Set([
          ...form.values.slidesReturned,
          ...specimenSlideIds.filter((id) => !previouslyReturnedSlides.has(id)),
        ])
      );
      // Set default conditions for newly added items
      const newConditions = { ...form.values.itemConditions };
      specimenBlockIds.forEach((id) => {
        if (!newConditions[id]) newConditions[id] = "good";
      });
      specimenSlideIds.forEach((id) => {
        if (!newConditions[id]) newConditions[id] = "good";
      });
      form.setValues({
        ...form.values,
        blocksReturned: newBlocks,
        slidesReturned: newSlides,
        itemConditions: newConditions,
      });
    } else {
      // Remove all blocks and slides for this specimen
      form.setValues({
        ...form.values,
        blocksReturned: form.values.blocksReturned.filter(
          (id) => !specimenBlockIds.includes(id)
        ),
        slidesReturned: form.values.slidesReturned.filter(
          (id) => !specimenSlideIds.includes(id)
        ),
      });
    }
  };

  // Handle slide checkbox change - unchecking also unchecks parent block
  const handleSlideChange = (block: any, slideId: string, checked: boolean) => {
    const blockSlideIds = getBlockSlideIds(block);

    if (checked) {
      // Add slide
      const newSlides = Array.from(
        new Set([...form.values.slidesReturned, slideId])
      );
      const newConditions = { ...form.values.itemConditions };
      if (!newConditions[slideId]) newConditions[slideId] = "good";

      // Check if all slides in block are now selected - if so, select the block too
      const allBlockSlidesSelected = blockSlideIds.every(
        (id) => newSlides.includes(id) || previouslyReturnedSlides.has(id)
      );
      let newBlocks = form.values.blocksReturned;
      if (allBlockSlidesSelected && blocksSent.includes(block.id)) {
        newBlocks = Array.from(
          new Set([...form.values.blocksReturned, block.id])
        );
        if (!newConditions[block.id]) newConditions[block.id] = "good";
      }

      form.setValues({
        ...form.values,
        blocksReturned: newBlocks,
        slidesReturned: newSlides,
        itemConditions: newConditions,
      });
    } else {
      // Remove slide and also uncheck the block (since not all slides are returned)
      form.setValues({
        ...form.values,
        blocksReturned: form.values.blocksReturned.filter(
          (id) => id !== block.id
        ),
        slidesReturned: form.values.slidesReturned.filter(
          (id) => id !== slideId
        ),
      });
    }
  };

  // Check if a specimen is fully selected (all blocks and slides)
  const isSpecimenFullySelected = (item: any): boolean => {
    const specimenBlockIds = getSpecimenBlockIds(item);
    const specimenSlideIds = getSpecimenSlideIds(item);

    const allBlocksSelected = specimenBlockIds.every(
      (id) =>
        form.values.blocksReturned.includes(id) ||
        previouslyReturnedBlocks.has(id)
    );
    const allSlidesSelected = specimenSlideIds.every(
      (id) =>
        form.values.slidesReturned.includes(id) ||
        previouslyReturnedSlides.has(id)
    );
    return allBlocksSelected && allSlidesSelected;
  };

  // Check if a specimen is partially selected
  const isSpecimenPartiallySelected = (item: any): boolean => {
    const specimenBlockIds = getSpecimenBlockIds(item);
    const specimenSlideIds = getSpecimenSlideIds(item);

    const anyBlockSelected = specimenBlockIds.some(
      (id) =>
        form.values.blocksReturned.includes(id) ||
        previouslyReturnedBlocks.has(id)
    );
    const anySlideSelected = specimenSlideIds.some(
      (id) =>
        form.values.slidesReturned.includes(id) ||
        previouslyReturnedSlides.has(id)
    );
    return (
      (anyBlockSelected || anySlideSelected) && !isSpecimenFullySelected(item)
    );
  };

  // Check if a block is fully selected (all slides)
  const isBlockFullySelected = (block: any): boolean => {
    const blockSlideIds = getBlockSlideIds(block);
    if (blockSlideIds.length === 0) {
      // Block with no slides - just check if block itself is selected
      return (
        form.values.blocksReturned.includes(block.id) ||
        previouslyReturnedBlocks.has(block.id)
      );
    }
    return blockSlideIds.every(
      (id) =>
        form.values.slidesReturned.includes(id) ||
        previouslyReturnedSlides.has(id)
    );
  };

  // Check if a block is partially selected (some but not all slides)
  const isBlockPartiallySelected = (block: any): boolean => {
    const blockSlideIds = getBlockSlideIds(block);
    if (blockSlideIds.length === 0) {
      return false;
    }
    const anySlideSelected = blockSlideIds.some(
      (id) =>
        form.values.slidesReturned.includes(id) ||
        previouslyReturnedSlides.has(id)
    );
    return anySlideSelected && !isBlockFullySelected(block);
  };

  // Check if any file is currently uploading
  const isUploading = reportFiles.some(
    (f) => !f.s3Key && !f.uploadError && f.uploadProgress !== undefined
  );

  const handleSubmit = (values: ReceiptFormValues) => {
    // Prevent submission while upload is in progress
    if (isUploading) {
      notifications.show({
        title: "Upload in Progress",
        message:
          "Please wait for the file upload to complete before submitting",
        color: "yellow",
      });
      return;
    }

    const finalValues = {
      ...values,
      notes: notesEditor ? notesEditor.getHTML() : values.notes,
      externalDiagnosis:
        !isExternalInstitution && diagnosisEditor
          ? diagnosisEditor.getHTML()
          : values.externalDiagnosis,
    };

    setPendingValues(finalValues);
    setView("confirm");
  };

  const handleConfirmSubmit = async () => {
    if (!pendingValues) return;
    const values = pendingValues;
    const isTissueCypherReportReceipt =
      sendOut.workflowKind === "tissue_cypher" && Boolean(uploadedFileKey);
    const isPartial =
      !isTissueCypherReportReceipt &&
      totalSent > 0 &&
      values.slidesReturned.length + values.blocksReturned.length < totalSent;
    let diagnosisText = values.externalDiagnosis;
    if (isExternalStain && values.stainResult) {
      diagnosisText = `Result: ${values.stainResult.toUpperCase()}${
        values.stainResultText ? `\n${values.stainResultText}` : ""
      }`;
    }
    try {
      await logReceiptMutation.mutateAsync({
        id: sendOut.id,
        data: {
          receivedById: values.receivedById || undefined,
          receivedDate: values.receivedDate
            ? values.receivedDate.toISOString()
            : undefined,
          slidesReturned: values.slidesReturned,
          blocksReturned: values.blocksReturned,
          materialsCondition: JSON.stringify(values.itemConditions),
          externalReportFileKey: uploadedFileKey || undefined,
          externalDiagnosis: diagnosisText || undefined,
          externalReferenceNumber: values.externalReferenceNumber || undefined,
          externalInterpretation: values.externalInterpretation || undefined,
          notes: values.notes || undefined,
        },
        isPartial,
      });
      notifications.show({
        title: "Receipt Logged",
        message: `Receipt logged for ${sendOut.case?.caseNumber || "case"}${
          isPartial ? " (partial)" : ""
        }`,
        color: "green",
      });
      onSuccess?.();
    } catch (error: any) {
      const message = error?.response?.data?.message || "Failed to log receipt";
      showValidationNotification(message);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group>
          <IconInbox size={20} />
          <Text fw={600}>
            {view === "confirm" ? "Confirm Receipt" : "Log Receipt"} -{" "}
            {getReceiptRequestTypeLabel(sendOut?.requestType)}
          </Text>
        </Group>
      }
      size={1200}
      closeOnClickOutside={false}
    >
      {view === "form" && (
        <form
          noValidate
          onSubmit={form.onSubmit(handleSubmit, () => scrollToFirstError(form))}
        >
          <ScrollArea.Autosize mah="calc(80vh - 150px)" offsetScrollbars>
            <Stack gap="sm" p={4}>
              {/* Header row */}
              <Group gap="xl">
                <Box>
                  <Text size="xs" c="dimmed">
                    Case
                  </Text>
                  <Text fw={600}>{sendOut.case?.caseNumber || "N/A"}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">
                    Send-Out Date
                  </Text>
                  <Text fw={500} size="sm">
                    {sendOut.shippedAt
                      ? dayjs(sendOut.shippedAt).format("MMM D, YYYY")
                      : "N/A"}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">
                    Destination
                  </Text>
                  <Text fw={500} size="sm">
                    {sendOut.externalInstitution?.name || "N/A"}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">
                    Materials Sent
                  </Text>
                  <Text fw={500} size="sm">
                    {slidesSent.length} slide
                    {slidesSent.length !== 1 ? "s" : ""} across{" "}
                    {resolvedMaterials.reduce(
                      (acc: number, item: any) =>
                        acc + Number(item.blocks.length),
                      0
                    ) || blocksSent.length}{" "}
                    block
                    {(resolvedMaterials.reduce(
                      (acc: number, item: any) =>
                        acc + Number(item.blocks.length),
                      0
                    ) || blocksSent.length) !== 1
                      ? "s"
                      : ""}
                  </Text>
                </Box>
              </Group>

              {/* Two-column layout: Materials (left) | Report & Options (right) */}
              <Group align="flex-start" gap={0} wrap="nowrap" mt="md">
                {/* Left: Materials Returned - specimen card layout */}
                <Box style={{ flex: 1, paddingRight: 16 }}>
                  <Text fw={600} size="sm" mb="xs">
                    Materials Returned
                  </Text>

                  {/* Barcode Scanner Input */}
                  <TextInput
                    placeholder="Scan slide barcode..."
                    description="Scan barcode to mark slide or block as returned"
                    leftSection={<IconScan size={16} />}
                    value={scanInput}
                    onChange={(e) => setScanInput(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleBarcodeScan();
                      }
                    }}
                    size="sm"
                    w="100%"
                    mb="sm"
                  />

                  {resolvedMaterials.length > 0 ? (
                    <Stack gap="sm">
                      {resolvedMaterials.map((item: any) => {
                        const specimenBlockIds = getSpecimenBlockIds(item);
                        const specimenSlideIds = getSpecimenSlideIds(item);
                        const hasSelectableItems =
                          specimenBlockIds.length > 0 ||
                          specimenSlideIds.length > 0;
                        const specimenFullySelected =
                          isSpecimenFullySelected(item);
                        const specimenPartiallySelected =
                          isSpecimenPartiallySelected(item);
                        const allPreviouslyReturned =
                          specimenBlockIds.every((id) =>
                            previouslyReturnedBlocks.has(id)
                          ) &&
                          specimenSlideIds.every((id) =>
                            previouslyReturnedSlides.has(id)
                          );

                        return (
                          <Paper
                            key={item.specimen.id}
                            withBorder
                            p="xs"
                            radius="sm"
                          >
                            <Group gap="xs" mb={6}>
                              {hasSelectableItems ? (
                                <Checkbox
                                  checked={specimenFullySelected}
                                  indeterminate={specimenPartiallySelected}
                                  disabled={allPreviouslyReturned}
                                  onChange={(e) =>
                                    handleSpecimenChange(
                                      item,
                                      e.currentTarget.checked
                                    )
                                  }
                                  label={
                                    <Group gap="xs">
                                      <IconFlask
                                        size={14}
                                        color="var(--mantine-color-indigo-6)"
                                      />
                                      <Text fw={600} size="sm">
                                        Specimen {item.specimen.number}
                                      </Text>
                                      {item.specimen.source && (
                                        <Badge
                                          color="indigo"
                                          variant="light"
                                          size="xs"
                                        >
                                          {item.specimen.source.name}
                                        </Badge>
                                      )}
                                    </Group>
                                  }
                                  size="xs"
                                />
                              ) : (
                                <>
                                  <IconFlask
                                    size={14}
                                    color="var(--mantine-color-indigo-6)"
                                  />
                                  <Text fw={600} size="sm">
                                    Specimen {item.specimen.number}
                                  </Text>
                                  {item.specimen.source && (
                                    <Badge
                                      color="indigo"
                                      variant="light"
                                      size="xs"
                                    >
                                      {item.specimen.source.name}
                                    </Badge>
                                  )}
                                </>
                              )}
                            </Group>
                            {item.blocks.map((block: any) => {
                              const blockSlideIds = getBlockSlideIds(block);
                              const blockFullySelected =
                                isBlockFullySelected(block);
                              const blockPartiallySelected =
                                isBlockPartiallySelected(block);
                              const blockPrevReturned =
                                previouslyReturnedBlocks.has(block.id);
                              const allSlidesPrevReturned = blockSlideIds.every(
                                (id) => previouslyReturnedSlides.has(id)
                              );
                              const blockDisabled =
                                blockPrevReturned && allSlidesPrevReturned;

                              return (
                                <Box key={block.id} ml="sm" mb={4}>
                                  {/* Block checkbox with condition */}
                                  {blocksSent.includes(block.id) &&
                                    (() => {
                                      return (
                                        <Group gap="xs" wrap="nowrap" mb={2}>
                                          <Checkbox
                                            checked={
                                              blockFullySelected ||
                                              blockPrevReturned
                                            }
                                            indeterminate={
                                              blockPartiallySelected
                                            }
                                            disabled={blockDisabled}
                                            onChange={(e) => {
                                              if (blockDisabled) return;
                                              handleBlockChange(
                                                block,
                                                e.currentTarget.checked
                                              );
                                            }}
                                            label={
                                              <Group gap={4}>
                                                <IconCube
                                                  size={10}
                                                  color="gray"
                                                />
                                                <Text
                                                  size="xs"
                                                  fw={500}
                                                  c={
                                                    blockDisabled
                                                      ? "dimmed"
                                                      : undefined
                                                  }
                                                >
                                                  Block {block.number}
                                                </Text>
                                                {blockPrevReturned && (
                                                  <Text
                                                    size="xs"
                                                    c="dimmed"
                                                    fs="italic"
                                                  >
                                                    returned
                                                  </Text>
                                                )}
                                              </Group>
                                            }
                                            size="xs"
                                          />
                                          {blockFullySelected &&
                                            !blockPrevReturned && (
                                              <Group gap={4}>
                                                <Text size="xs" c="dimmed">
                                                  Condition:
                                                </Text>
                                                <Select
                                                  data={CONDITION_OPTIONS}
                                                  value={
                                                    form.values.itemConditions[
                                                      block.id
                                                    ] || "good"
                                                  }
                                                  onChange={(val) =>
                                                    form.setFieldValue(
                                                      "itemConditions",
                                                      {
                                                        ...form.values
                                                          .itemConditions,
                                                        [block.id]:
                                                          val || "good",
                                                      }
                                                    )
                                                  }
                                                  size="xs"
                                                  w={100}
                                                  allowDeselect={false}
                                                />
                                              </Group>
                                            )}
                                        </Group>
                                      );
                                    })()}
                                  {!blocksSent.includes(block.id) && (
                                    <Group gap={4} mb={2} ml={2}>
                                      <IconCube size={10} color="gray" />
                                      <Text size="xs" fw={500}>
                                        Block {block.number}
                                      </Text>
                                    </Group>
                                  )}
                                  {/* Slide checkboxes with condition */}
                                  {block.slides.length > 0 && (
                                    <Stack gap={4} ml="lg">
                                      {block.slides.map((slide: any) => {
                                        const isChecked =
                                          form.values.slidesReturned.includes(
                                            slide.id
                                          );
                                        const wasPrevReturned =
                                          previouslyReturnedSlides.has(
                                            slide.id
                                          );
                                        return (
                                          <Group
                                            key={slide.id}
                                            gap="xs"
                                            wrap="nowrap"
                                          >
                                            <Checkbox
                                              checked={
                                                isChecked || wasPrevReturned
                                              }
                                              disabled={wasPrevReturned}
                                              onChange={(e) => {
                                                if (wasPrevReturned) return;
                                                handleSlideChange(
                                                  block,
                                                  slide.id,
                                                  e.currentTarget.checked
                                                );
                                              }}
                                              label={
                                                <Group gap={4}>
                                                  <Text
                                                    size="xs"
                                                    c={
                                                      wasPrevReturned
                                                        ? "dimmed"
                                                        : undefined
                                                    }
                                                  >
                                                    Slide {slide.number}
                                                  </Text>
                                                  {slide.stain && (
                                                    <Badge
                                                      size="xs"
                                                      variant="outline"
                                                      color="dark"
                                                    >
                                                      {slide.stain.code}
                                                    </Badge>
                                                  )}
                                                  {wasPrevReturned && (
                                                    <Text
                                                      size="xs"
                                                      c="dimmed"
                                                      fs="italic"
                                                    >
                                                      returned
                                                    </Text>
                                                  )}
                                                </Group>
                                              }
                                              size="xs"
                                            />
                                            {isChecked && !wasPrevReturned && (
                                              <Group gap={4}>
                                                <Text size="xs" c="dimmed">
                                                  Condition:
                                                </Text>
                                                <Select
                                                  data={CONDITION_OPTIONS}
                                                  value={
                                                    form.values.itemConditions[
                                                      slide.id
                                                    ] || "good"
                                                  }
                                                  onChange={(val) =>
                                                    form.setFieldValue(
                                                      "itemConditions",
                                                      {
                                                        ...form.values
                                                          .itemConditions,
                                                        [slide.id]:
                                                          val || "good",
                                                      }
                                                    )
                                                  }
                                                  size="xs"
                                                  w={100}
                                                  allowDeselect={false}
                                                />
                                              </Group>
                                            )}
                                          </Group>
                                        );
                                      })}
                                    </Stack>
                                  )}
                                </Box>
                              );
                            })}
                          </Paper>
                        );
                      })}
                    </Stack>
                  ) : totalSent === 0 ? (
                    <Text size="sm" c="dimmed">
                      No materials were recorded
                    </Text>
                  ) : (
                    <Text size="sm" c="dimmed">
                      {slidesSent.length} slide
                      {slidesSent.length !== 1 ? "s" : ""} across{" "}
                      {blocksSent.length} block
                      {blocksSent.length !== 1 ? "s" : ""}
                    </Text>
                  )}
                </Box>

                {/* Vertical Divider */}
                <Divider orientation="vertical" />

                {/* Right: Status, Report, Diagnosis, Received, QA, Notes */}
                <Box style={{ flex: 1, paddingLeft: 16 }}>
                  {/* 1. Receipt Status */}
                  <Box mb="sm">
                    {totalSent > 0 && totalReturned < totalSent ? (
                      <Badge color="yellow" variant="light" size="lg">
                        Partial - {totalReturned}/{totalSent} returned
                      </Badge>
                    ) : (
                      <Badge color="green" variant="light" size="lg">
                        Complete
                      </Badge>
                    )}
                  </Box>

                  {/* 2. External Report Upload (optional for all types) */}
                  <Text fw={600} size="sm" mb="xs">
                    External Report
                  </Text>
                  <Box mb="sm">
                    <DocumentUpload
                      files={reportFiles}
                      onFilesChange={setReportFiles}
                      maxFiles={1}
                      maxSize={50}
                      getPresignedUrl={sendOutsService.getReportUploadUrl}
                      showDropZone
                      minHeight={120}
                      dropZonePlaceholder="Drop external report here"
                    />
                  </Box>

                  {/* 3. Reference Lab Case Number — hidden for external_institution */}
                  {!isExternalInstitution && (
                    <TextInput
                      label="Reference Lab Case Number"
                      placeholder="e.g., CSI-25-12345"
                      description="The case/accession number assigned by the reference lab"
                      size="sm"
                      mb="sm"
                      {...form.getInputProps("externalReferenceNumber")}
                    />
                  )}

                  {/* 4. Diagnosis / Result — hidden entirely for external_institution */}
                  {!isExternalInstitution && (
                    <Box mb="sm">
                      <Text size="sm" fw={500} mb={4}>
                        External Diagnosis Summary
                      </Text>
                      <RichTextEditor editor={diagnosisEditor}>
                        <RichTextEditor.Toolbar>
                          <RichTextEditor.ControlsGroup>
                            <RichTextEditor.Bold />
                            <RichTextEditor.Italic />
                            <RichTextEditor.Underline />
                          </RichTextEditor.ControlsGroup>
                          <RichTextEditor.ControlsGroup>
                            <RichTextEditor.BulletList />
                            <RichTextEditor.OrderedList />
                          </RichTextEditor.ControlsGroup>
                        </RichTextEditor.Toolbar>
                        <RichTextEditor.Content />
                      </RichTextEditor>
                    </Box>
                  )}

                  {/* 4. Received By */}
                  <Select
                    label="Received By"
                    placeholder="Select staff..."
                    data={users
                      .filter((u: any) => (u.isActive ?? u.is_active) !== false)
                      .map((u: any) => ({
                        value: u.id,
                        label: u.fullName || u.full_name || "Unknown",
                      }))
                      .sort((a: any, b: any) => a.label.localeCompare(b.label))}
                    searchable
                    size="sm"
                    mb="sm"
                    withAsterisk
                    {...form.getInputProps("receivedById")}
                  />

                  {/* 5. Received Date */}
                  <DateInput
                    label="Received Date"
                    size="sm"
                    mb="sm"
                    maxDate={new Date()}
                    withAsterisk
                    {...form.getInputProps("receivedDate")}
                  />

                  {/* 6. QA Reviewer (External Opinion only) */}
                  {!isExternalStain && (
                    <Select
                      label="QA Reviewer"
                      placeholder="Select QA reviewer..."
                      data={users
                        .filter(
                          (u: any) =>
                            (u.isActive ?? u.is_active) !== false &&
                            u.role?.name?.toLowerCase().includes("pathologist")
                        )
                        .map((u: any) => ({
                          value: u.id,
                          label: u.fullName || u.full_name || "Unknown",
                        }))
                        .sort((a: any, b: any) =>
                          a.label.localeCompare(b.label)
                        )}
                      searchable
                      clearable
                      size="sm"
                      mb="sm"
                      {...form.getInputProps("qaReviewerId")}
                    />
                  )}

                  {/* 7. Notes */}
                  <Box>
                    <Text size="sm" fw={500} mb={4}>
                      Notes
                    </Text>
                    <RichTextEditor editor={notesEditor}>
                      <RichTextEditor.Toolbar>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Bold />
                          <RichTextEditor.Italic />
                          <RichTextEditor.Underline />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.BulletList />
                          <RichTextEditor.OrderedList />
                        </RichTextEditor.ControlsGroup>
                      </RichTextEditor.Toolbar>
                      <RichTextEditor.Content />
                    </RichTextEditor>
                  </Box>
                </Box>
              </Group>
            </Stack>
          </ScrollArea.Autosize>

          {/* Sticky Footer */}
          <Group
            justify="space-between"
            pt="md"
            mt="md"
            style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
          >
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" color="teal">
              Review & Submit
            </Button>
          </Group>
        </form>
      )}

      {/* ===== CONFIRM VIEW ===== */}
      {view === "confirm" && pendingValues && (
        <>
          <ScrollArea.Autosize mah="calc(80vh - 150px)" offsetScrollbars>
            <Stack gap="sm" p={4}>
              <Alert
                color="teal"
                title="Review Receipt Details"
                icon={<IconCheck size={18} />}
              >
                Please review the details below before confirming.
              </Alert>

              {/* Receipt Status */}
              <Group>
                <Text size="sm" fw={500}>
                  Receipt Status:
                </Text>
                {totalSent > 0 &&
                pendingValues.slidesReturned.length +
                  pendingValues.blocksReturned.length <
                  totalSent ? (
                  <Badge color="yellow" variant="light">
                    Partial -{" "}
                    {totalSent -
                      (pendingValues.slidesReturned.length +
                        pendingValues.blocksReturned.length)}{" "}
                    item(s) pending
                  </Badge>
                ) : (
                  <Badge color="green" variant="light">
                    Complete
                  </Badge>
                )}
              </Group>

              {/* Materials Summary */}
              <Box>
                <Text size="sm" fw={500} mb="xs">
                  Materials Returned
                </Text>
                {resolvedMaterials.length > 0 ? (
                  <Group align="stretch" gap="sm" wrap="wrap">
                    {resolvedMaterials.map((item: any) => (
                      <Paper
                        key={item.specimen.id}
                        withBorder
                        p="xs"
                        radius="sm"
                        style={{ minWidth: 150 }}
                      >
                        <Group gap="xs" mb={4}>
                          <IconFlask
                            size={14}
                            color="var(--mantine-color-indigo-6)"
                          />
                          <Text fw={600} size="sm">
                            Specimen {item.specimen.number}
                          </Text>
                          {item.specimen.source && (
                            <Badge color="indigo" variant="light" size="xs">
                              {item.specimen.source.name}
                            </Badge>
                          )}
                        </Group>
                        {item.blocks.map((block: any) => (
                          <Box key={block.id} ml="sm" mb={2}>
                            {blocksSent.includes(block.id) && (
                              <Group gap={4} mb={2}>
                                <IconCube size={10} color="gray" />
                                <Text size="xs" fw={500}>
                                  Block {block.number}
                                </Text>
                                {pendingValues.blocksReturned.includes(
                                  block.id
                                ) ? (
                                  <Badge
                                    size="xs"
                                    color={getConditionBadgeColor(
                                      pendingValues.itemConditions[block.id]
                                    )}
                                    variant="light"
                                  >
                                    {pendingValues.itemConditions[block.id] ||
                                      "good"}
                                  </Badge>
                                ) : (
                                  <Badge size="xs" color="red" variant="light">
                                    not returned
                                  </Badge>
                                )}
                              </Group>
                            )}
                            {block.slides.length > 0 && (
                              <Stack gap={1} ml="md">
                                {block.slides.map((slide: any) => (
                                  <Group key={slide.id} gap={4}>
                                    <Text size="xs">Slide {slide.number}</Text>
                                    {slide.stain && (
                                      <Badge
                                        size="xs"
                                        variant="outline"
                                        color="dark"
                                      >
                                        {slide.stain.code}
                                      </Badge>
                                    )}
                                    {pendingValues.slidesReturned.includes(
                                      slide.id
                                    ) ? (
                                      <Badge
                                        size="xs"
                                        color={getConditionBadgeColor(
                                          pendingValues.itemConditions[slide.id]
                                        )}
                                        variant="light"
                                      >
                                        {pendingValues.itemConditions[
                                          slide.id
                                        ] || "good"}
                                      </Badge>
                                    ) : (
                                      <Badge
                                        size="xs"
                                        color="red"
                                        variant="light"
                                      >
                                        not returned
                                      </Badge>
                                    )}
                                  </Group>
                                ))}
                              </Stack>
                            )}
                          </Box>
                        ))}
                      </Paper>
                    ))}
                  </Group>
                ) : (
                  <Text size="sm" c="dimmed">
                    No materials recorded
                  </Text>
                )}
              </Box>

              {/* Other details */}
              <Divider />
              <Group>
                <Box>
                  <Text size="xs" c="dimmed">
                    Received By
                  </Text>
                  <Text size="sm" fw={500}>
                    {users.find((u: any) => u.id === pendingValues.receivedById)
                      ?.fullName ||
                      users.find(
                        (u: any) => u.id === pendingValues.receivedById
                      )?.full_name ||
                      profile?.fullName ||
                      "Current User"}
                  </Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">
                    Received Date
                  </Text>
                  <Text size="sm" fw={500}>
                    {pendingValues.receivedDate
                      ? dayjs(pendingValues.receivedDate).format("MMM D, YYYY")
                      : "Today"}
                  </Text>
                </Box>
              </Group>

              {pendingValues.externalDiagnosis && (
                <Box>
                  <Text size="xs" c="dimmed">
                    External Diagnosis
                  </Text>
                  <div
                    style={{ fontSize: "var(--mantine-font-size-sm)" }}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                      __html: pendingValues.externalDiagnosis,
                    }}
                  />
                </Box>
              )}

              {isExternalStain && pendingValues.stainResult && (
                <Box>
                  <Text size="xs" c="dimmed">
                    Stain Result
                  </Text>
                  <Badge size="sm" variant="light">
                    {pendingValues.stainResult.toUpperCase()}
                  </Badge>
                </Box>
              )}

              {pendingValues.externalReferenceNumber && (
                <Box>
                  <Text size="xs" c="dimmed">
                    Reference Lab Case Number
                  </Text>
                  <Text size="sm" fw={500}>
                    {pendingValues.externalReferenceNumber}
                  </Text>
                </Box>
              )}

              {pendingValues.externalInterpretation && (
                <Box>
                  <Text size="xs" c="dimmed">
                    Interpretation
                  </Text>
                  <Text size="sm">{pendingValues.externalInterpretation}</Text>
                </Box>
              )}

              {uploadedFileName && (
                <Box>
                  <Text size="xs" c="dimmed">
                    Report Uploaded
                  </Text>
                  <Group gap="xs">
                    <IconCheck size={14} color="green" />
                    <Text size="sm">{uploadedFileName}</Text>
                  </Group>
                </Box>
              )}

              {pendingValues.notes && (
                <Box>
                  <Text size="xs" c="dimmed">
                    Notes
                  </Text>
                  <div
                    style={{ fontSize: "var(--mantine-font-size-sm)" }}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: pendingValues.notes }}
                  />
                </Box>
              )}
            </Stack>
          </ScrollArea.Autosize>

          <Group
            justify="space-between"
            pt="md"
            mt="md"
            style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}
          >
            <Button variant="subtle" onClick={() => setView("form")}>
              Back
            </Button>
            <Button
              color="teal"
              onClick={handleConfirmSubmit}
              loading={logReceiptMutation.isPending}
            >
              {isExternalStain
                ? "Confirm & Save Results"
                : "Confirm & Log Receipt"}
            </Button>
          </Group>
        </>
      )}
    </Modal>
  );
}
